import { useTranslation } from 'react-i18next'
import useToastStore from '@/stores/useToastStore/useToastStore'
import {
    useReportsApi,
    useSustainabilityApi,
    TotalEmissionsReportSettings,
} from '@/hooks'
import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useSustainabilityFiltersGetParams } from '@/features/Sustainability/hooks/useSustainabilityFiltersGetParams'
import { mapSustainabilityParamsToFilters } from '@/features/Sustainability/utils/mapSustainabilityParamsToFilters'
import { processSustainabilityFilters } from '../utils/processSustainabilityFilters'
import {
    DEFAULT_SUSTAINABILITY_FILTERING,
    DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS,
} from '@/utils/constants'

export const useExportTotalEmissionsReport = (reportId: string) => {
    const addAlert = useToastStore((state) => state.addAlert)
    const { t } = useTranslation('translations')
    const triggered = useRef(false)

    const { useGetReport } = useReportsApi()
    const { data: report, refetch: refetchReport } = useGetReport(reportId, {
        enabled: false,
    })

    const { useTotalEmissionsQuery } = useSustainabilityApi()

    // Extract and validate report settings
    const reportSettings = useMemo((): TotalEmissionsReportSettings => {
        if (
            report?.settings &&
            typeof report.settings === 'object' &&
            'type' in report.settings &&
            'scope' in report.settings
        ) {
            return report.settings
        }
        return DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS
    }, [report?.settings])

    // Convert report filters to SustainabilityFiltersValues using Sustainability utils
    const sustainabilityFilters = useMemo(() => {
        if (!report?.filters) {
            return DEFAULT_SUSTAINABILITY_FILTERING
        }

        // Merge settings with filters for processing
        const mergedFilters = {
            ...report.filters,
            type: reportSettings.type,
            scope: reportSettings.scope,
            scope2type: reportSettings.scope2type,
        }

        // Convert CustomReportFiltersParams -> GetSustainabilityTotalEmissionsRequestParams
        const processedParams = processSustainabilityFilters(mergedFilters)

        // Convert GetSustainabilityTotalEmissionsRequestParams -> SustainabilityFiltersValues
        return mapSustainabilityParamsToFilters(processedParams)
    }, [report?.filters, reportSettings])

    const processedFilters = useSustainabilityFiltersGetParams(
        sustainabilityFilters,
        reportSettings
    )

    const {
        data: totalEmissions,
        refetch: refetchTotalEmissions,
        downloadXlsx,
    } = useTotalEmissionsQuery(processedFilters, {
        enabled: !!report && triggered.current,
    })

    const downloadData = useCallback(() => {
        downloadXlsx().catch(() => {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        })
    }, [addAlert, downloadXlsx, t])

    useEffect(() => {
        if (triggered.current) {
            triggered.current = false
            downloadData()
        }
    }, [downloadData, totalEmissions])

    const exportTotalEmissionsReport = () => {
        triggered.current = true

        if (!report) {
            void refetchReport()
            return
        }

        if (!totalEmissions) {
            void refetchTotalEmissions()
            return
        }

        triggered.current = false
        downloadData()
    }

    return {
        exportTotalEmissionsReport,
    }
}
